import { CREATOR_TAG_COLORS, type CreatorTagColor } from "@unbox/api";
import { Check } from "@untitledui/icons";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Returns black or white depending on which contrasts better against the
 * given hex background, so a tag's name stays readable on its pill.
 */
export const contrastText = (hex: string): "#ffffff" | "#000000" => {
  const value = hex.replace("#", "");
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  // Relative luminance (sRGB) — threshold ~0.6 reads well for this palette.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#000000" : "#ffffff";
};

/**
 * A colour swatch that opens a palette popover. The palette is the fixed,
 * server-enforced set from `CREATOR_TAG_COLORS`.
 */
export const TagColorPicker = ({
  color,
  onChange,
  disabled,
}: {
  color: string;
  onChange: (color: CreatorTagColor) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        disabled={disabled}
        aria-label="Choose colour"
        className="size-5 shrink-0 rounded-full ring-1 ring-black/10 ring-inset transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: color }}
      />
      <PopoverContent align="start" className="w-auto p-2">
        <div className="grid grid-cols-6 gap-1.5">
          {CREATOR_TAG_COLORS.map((swatch) => (
            <button
              key={swatch}
              type="button"
              aria-label={swatch}
              onClick={() => {
                onChange(swatch);
                setOpen(false);
              }}
              className="flex size-7 items-center justify-center rounded-full ring-1 ring-black/10 ring-inset transition-transform hover:scale-110"
              style={{ backgroundColor: swatch }}
            >
              {swatch === color && <Check className="size-4" style={{ color: contrastText(swatch) }} />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
